import { useMemo } from 'react'
import moment from 'moment'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'

import { EmptyObject } from 'lib/object/objectUtils'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import Group from 'components/utils/Group'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OfferTilePricing from 'components/OfferList/OfferListTiles/Support/OfferTilePricing'
import SearchTileFlightOnlyPricing from 'components/OfferList/OfferListTiles/Support/SearchTileFlightOnlyPricing'
import SearchTileWalledMessage from '../shared/SearchTileWalledMessage'
import WalledContent from 'components/Common/WalledContent'
import { calculateOfferTileDiscountPercentage } from 'lib/search/searchUtils'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { MALDIVES_BUNDLE_DEFAULT_GUESTS, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import CharterBundlePricing from './CharterBundlePricing'

interface Props {
  bestPackage?: App.HotelPackage
  filters?: App.OfferListFilters
  offer: App.HotelOffer | App.HotelOfferSummary | App.VillaOffer | App.VillaOfferSummary
  bestRate?: App.OfferAvailableRate
  duration?: number
  hidePrice?: boolean
  showPerNightPricing?: boolean
  hideIncludesTaxesAndFees?: boolean
  disableCharterPricing?: boolean
}

function HotelSearchTilePriceStack({
  bestPackage,
  filters,
  offer,
  bestRate,
  duration,
  hidePrice = false,
  showPerNightPricing = false,
  hideIncludesTaxesAndFees = false,
  disableCharterPricing = false,
}: Props) {
  const hasDates = !!(filters?.checkIn && filters?.checkOut && filters?.rooms?.length)
  const checkInDate = useMemo(() => {
    return filters?.checkIn ? moment(filters.checkIn) : undefined
  }, [filters?.checkIn])

  const directSearchPrices = useDirectSearchPrices({ filters: filters ?? EmptyObject, offerId: offer.id })
  const minimumDurationPackage = useMemo<App.HotelPackage | undefined>(() => {
    if (!isFlashHotelOffer(offer) || !offer.minDuration) {
      return undefined
    }

    return offer.packages
      .filter((pkg) => pkg.duration === offer.minDuration && pkg.price > 0)
      .reduce<App.HotelPackage | undefined>((cheapestPackage, pkg) => {
        if (!cheapestPackage || pkg.price < cheapestPackage.price) {
          return pkg
        }

        return cheapestPackage
      }, undefined)
  }, [offer])
  const shouldUseAgentHubFlashOfferMinStayPricing = isAgentHubEnabled && isFlashHotelOffer(offer) && !!minimumDurationPackage
  const pricingPackage = shouldUseAgentHubFlashOfferMinStayPricing ? minimumDurationPackage : bestPackage
  const pricingSearchPrices = shouldUseAgentHubFlashOfferMinStayPricing ? undefined : directSearchPrices
  const pricingFilters = useMemo(() => {
    if (!shouldUseAgentHubFlashOfferMinStayPricing || !filters || !pricingPackage?.roomRate?.id || !pricingPackage.roomType?.id) {
      return filters
    }

    return {
      ...filters,
      roomRateId: pricingPackage.roomRate.id,
      roomTypeId: pricingPackage.roomType.id,
    }
  }, [filters, pricingPackage, shouldUseAgentHubFlashOfferMinStayPricing])
  const discountPercentage = useMemo(() => {
    if (offer.isDiscountPillHidden) {
      return undefined
    }

    if (pricingSearchPrices?.lowestPrice && pricingSearchPrices?.lowestPriceValue) {
      return calculateOfferTileDiscountPercentage({
        price: pricingSearchPrices.lowestPrice,
        value: pricingSearchPrices.lowestPriceValue,
      })
    }

    if (pricingPackage) {
      return calculateOfferTileDiscountPercentage({
        price: pricingPackage.price,
        value: pricingPackage.value,
      })
    }

    return undefined
  }, [offer, pricingPackage, pricingSearchPrices])

  const offerMetaData = useOfferMetaData(offer.id, filters)
  const { isPricePerPerson, duration: contextDuration } = useMaldivesDirectBundleContext()

  const isCharterBundleOffer = isFlashHotelOffer(offer) &&
    offer.offerFlightsEnabled &&
    offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)

  const charterSearchPrices = offerMetaData?.pricing
  if (!disableCharterPricing && isCharterBundleOffer && charterSearchPrices?.lowestPrice) {
    const totalGuests = filters?.rooms ? sumUpOccupancies(filters.rooms) : MALDIVES_BUNDLE_DEFAULT_GUESTS
    const bundleDiscountPercent = charterSearchPrices.lowestPriceValue
      ? getValueOffPercent(charterSearchPrices.lowestPriceValue, charterSearchPrices.lowestPrice)
      : undefined
    const bundleMemberDiscountPercent = charterSearchPrices.lowestPriceValue && charterSearchPrices.lowestMemberPrice
      ? getValueOffPercent(charterSearchPrices.lowestPriceValue, charterSearchPrices.lowestMemberPrice)
      : undefined
    return <CharterBundlePricing
      offer={offer}
      pricing={{
        price: charterSearchPrices.lowestPrice,
        memberPrice: charterSearchPrices.lowestMemberPrice,
        value: charterSearchPrices.lowestPriceValue,
        discountPercent: bundleDiscountPercent,
        memberDiscountPercent: bundleMemberDiscountPercent,
        totalGuests,
        roomCount: filters?.rooms?.length,
      }}
      isPricePerPerson={isPricePerPerson}
      nights={contextDuration ?? charterSearchPrices.duration ?? duration}
      filters={filters}
    />
  }

  return <div>
    {!pricingPackage && <LoadingIndicator inline ballSize={12} />}
    {pricingPackage && <WalledContent
      enforced={offer.walledGarden}
      wall={<SearchTileWalledMessage discountPercentage={discountPercentage} />}
    >
      <Group direction="vertical" gap={8} fullWidth>
        {!offer.bundledWithFlightsOnly && <OfferTilePricing
          offer={offer}
          hasDates={hasDates}
          checkInDate={checkInDate}
          pkg={pricingPackage}
          availableRate={shouldUseAgentHubFlashOfferMinStayPricing ? undefined : bestRate}
          align="start"
          filters={pricingFilters}
          duration={shouldUseAgentHubFlashOfferMinStayPricing ? pricingPackage.duration : duration}
          hidePrice={hidePrice}
          overrideRoomsNumber={offerMetaData?.rooms?.length}
          isPricePerNight={showPerNightPricing}
          hideIncludesTaxesAndFees={hideIncludesTaxesAndFees}
        />}
        {(offer.bundledWithFlightsOnly && !hidePrice) && (
          <SearchTileFlightOnlyPricing
            offer={offer}
            rooms={filters?.rooms}
            hasDates={hasDates}
            pkg={pricingPackage}
            pricing={bestRate}
            filters={filters}
          />
        )}
      </Group>
    </WalledContent>}
  </div>
}

export default HotelSearchTilePriceStack
